'use client';

import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';

import { api } from '$lib/api/client';
import type { CompanyResponse } from '$lib/api/types';

type CompanyContextValue = {
	company: CompanyResponse | null;
	logoObjectUrl: string | null;
	loading: boolean;
	load: () => Promise<void>;
	setCompany: (company: CompanyResponse | null) => Promise<void>;
	clear: () => void;
};

const CompanyContext = createContext<CompanyContextValue | null>(null);

export function CompanyProvider({ children }: { children: ReactNode }) {
	const [company, setCompanyState] = useState<CompanyResponse | null>(null);
	const [logoObjectUrl, setLogoObjectUrl] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const versionRef = useRef(0);
	const logoRef = useRef<string | null>(null);

	const nextVersion = useCallback(() => {
		versionRef.current += 1;
		return versionRef.current;
	}, []);

	const revokeLogo = useCallback(() => {
		if (logoRef.current && typeof window !== 'undefined') {
			window.URL.revokeObjectURL(logoRef.current);
		}
		logoRef.current = null;
		setLogoObjectUrl(null);
	}, []);

	const syncLogo = useCallback(
		async (next: CompanyResponse | null, version: number) => {
			if (version !== versionRef.current) return;
			revokeLogo();
			if (!next?.logoUrl || typeof window === 'undefined') return;
			try {
				const objectUrl = await api.getObjectUrl(next.logoUrl);
				if (version !== versionRef.current) {
					window.URL.revokeObjectURL(objectUrl);
					return;
				}
				logoRef.current = objectUrl;
				setLogoObjectUrl(objectUrl);
			} catch {
				if (version === versionRef.current) setLogoObjectUrl(null);
			}
		},
		[revokeLogo]
	);

	const load = useCallback(async () => {
		const version = nextVersion();
		setLoading(true);
		try {
			const next = await api.get<CompanyResponse>('/companies/me');
			if (version !== versionRef.current) return;
			setCompanyState(next);
			await syncLogo(next, version);
		} catch {
			if (version === versionRef.current) {
				setCompanyState(null);
				revokeLogo();
			}
		} finally {
			if (version === versionRef.current) setLoading(false);
		}
	}, [nextVersion, revokeLogo, syncLogo]);

	const setCompany = useCallback(
		async (next: CompanyResponse | null) => {
			const version = nextVersion();
			setCompanyState(next);
			await syncLogo(next, version);
		},
		[nextVersion, syncLogo]
	);

	const clear = useCallback(() => {
		nextVersion();
		setCompanyState(null);
		revokeLogo();
		setLoading(false);
	}, [nextVersion, revokeLogo]);

	useEffect(() => () => revokeLogo(), [revokeLogo]);

	const value = useMemo(
		() => ({ company, logoObjectUrl, loading, load, setCompany, clear }),
		[company, logoObjectUrl, loading, load, setCompany, clear]
	);

	return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompany() {
	const context = useContext(CompanyContext);
	if (!context) throw new Error('useCompany must be used inside CompanyProvider');
	return context;
}
